import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Space, Popconfirm, Checkbox, Tag, Switch, Radio } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { listCustomEnumTypes, listCustomEnumValues, createCustomEnumValue, updateCustomEnumValue, createCustomEnumType, updateCustomEnumType, setCustomEnumTypeEnabled, setCustomEnumValueEnabled } from '../api/custom_enum';
import { listCustomFields, createCustomField, updateCustomField, setCustomFieldEnabled } from '../api/custom_field';
import { CustomEnumType, CustomEnumValue, CustomParamScenario, CustomEnumMode, CustomField } from '../api/model';

const CustomEnumManagement: React.FC = () => {
  const [enumTypes, setEnumTypes] = useState<CustomEnumType[]>([]);
  const [enumValues, setEnumValues] = useState<Record<string, CustomEnumValue[]>>({});
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [valueModalVisible, setValueModalVisible] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [fieldModalVisible, setFieldModalVisible] = useState(false);
  const [editingValue, setEditingValue] = useState<CustomEnumValue | null>(null);
  const [editingType, setEditingType] = useState<CustomEnumType | null>(null);
  const [editingField, setEditingField] = useState<CustomField | null>(null);
  const [currentTypeId, setCurrentTypeId] = useState<string>('');
  const [valueForm] = Form.useForm<CustomEnumValue>();
  const [typeForm] = Form.useForm<CustomEnumType>();
  const [fieldForm] = Form.useForm<CustomField>();

  // 获取所有自定义枚举类型
  const fetchEnumTypes = async () => {
    try {
      const response = await listCustomEnumTypes();
      if (response.success) {
        // 过滤掉全局类型（is_global为false的才是医生自定义的）
        const nonGlobalTypes = response.data.filter(type => !type.is_global);
        setEnumTypes(nonGlobalTypes);
        
        // 初始化每个类型的枚举值
        const values: Record<string, CustomEnumValue[]> = {};
        for (const type of nonGlobalTypes) {
          await fetchEnumValues(type.id);
        }
      } else {
        message.error('获取枚举类型失败: ' + response.message);
      }
    } catch (error) {
      message.error('获取枚举类型失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取指定类型的枚举值
  const fetchEnumValues = async (typeId: string) => {
    try {
      const response = await listCustomEnumValues(typeId);
      if (response.success) {
        setEnumValues(prev => ({ ...prev, [typeId]: response.data }));
      } else {
        message.error(`获取枚举值失败: ${response.message}`);
      }
    } catch (error) {
      message.error('获取枚举值失败');
    }
  };

  // 获取自定义字段列表
  const fetchCustomFields = async () => {
    try {
      const response = await listCustomFields();
      if (response.success) {
        setCustomFields(response.data);
      } else {
        message.error(`获取自定义字段失败: ${response.message}`);
      }
    } catch (error) {
      message.error('获取自定义字段失败');
    }
  };

  useEffect(() => {
    fetchEnumTypes();
    fetchCustomFields();
  }, []);

  // 处理创建枚举值
  const handleCreateValue = (typeId: string) => {
    setCurrentTypeId(typeId);
    setEditingValue(null);
    valueForm.resetFields();
    setValueModalVisible(true);
  };

  // 处理编辑枚举值
  const handleEditValue = (value: CustomEnumValue) => {
    setEditingValue(value);
    setCurrentTypeId(value.type_id.toString());
    valueForm.setFieldsValue({
      name: value.name,
      description: value.description,
      is_enabled: value.is_enabled
    });
    setValueModalVisible(true);
  };

  // 处理创建枚举类型
  const handleCreateType = () => {
    setEditingType(null);
    typeForm.resetFields();
    setTypeModalVisible(true);
  };

  // 处理编辑枚举类型
  const handleEditType = (type: CustomEnumType) => {
    setEditingType(type);
    typeForm.setFieldsValue({
      name: type.name,
      scenarios: type.scenarios,
      is_enabled: type.is_enabled
    });
    setTypeModalVisible(true);
  };

  // 处理枚举值启用状态变更
  const handleValueEnabledChange = async (value: CustomEnumValue, checked: boolean) => {
    try {
      const response = await setCustomEnumValueEnabled(value.id, checked);
      if (response.success) {
        message.success('更新枚举值启用状态成功');
        // 重新获取该类型的枚举值
        await fetchEnumValues(value.type_id.toString());
      } else {
        message.error('更新枚举值启用状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新枚举值启用状态失败:', error);
      message.error('更新枚举值启用状态失败');
    }
  };

  // 处理枚举类型启用状态变更
  const handleTypeEnabledChange = async (type: CustomEnumType, checked: boolean) => {
    try {
      const response = await setCustomEnumTypeEnabled(type.id, checked);
      if (response.success) {
        message.success('更新枚举类型启用状态成功');
        // 重新获取枚举类型
        await fetchEnumTypes();
      } else {
        message.error('更新枚举类型启用状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新枚举类型启用状态失败:', error);
      message.error('更新枚举类型启用状态失败');
    }
  };

  // 处理创建自定义字段
  const handleCreateField = () => {
    setEditingField(null);
    fieldForm.resetFields();
    setFieldModalVisible(true);
  };

  // 处理编辑自定义字段
  const handleEditField = (field: CustomField) => {
    setEditingField(field);
    fieldForm.setFieldsValue({
      name: field.name,
      hint: field.hint,
      scenarios: field.scenarios as CustomParamScenario[],
      is_enabled: field.is_enabled
    });
    setFieldModalVisible(true);
  };

  // 处理自定义字段启用状态变更
  const handleFieldEnabledChange = async (field: CustomField, checked: boolean) => {
    try {
      const response = await setCustomFieldEnabled(field.id, checked);
      if (response.success) {
        message.success('更新自定义字段启用状态成功');
        // 重新获取自定义字段
        await fetchCustomFields();
      } else {
        message.error('更新自定义字段启用状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新自定义字段启用状态失败:', error);
      message.error('更新自定义字段启用状态失败');
    }
  };

  // 在导入语句中添加新的API函数
  // 注意：这个修改需要在文件顶部添加
  // import { setCustomEnumTypeEnabled, setCustomEnumValueEnabled } from '../api/custom_enum';



  // 保存枚举值
  const handleSaveValue = async () => {
    try {
      const values = await valueForm.validateFields();
      
      if (editingValue) {
        // 更新枚举值
        const response = await updateCustomEnumValue(
          editingValue.id,
          values.name,
          values.description,
        );
        
        if (response.success) {
          message.success('更新成功');
          setValueModalVisible(false);
          valueForm.resetFields();
          // 重新获取该类型的枚举值
          await fetchEnumValues(editingValue.type_id.toString());
        } else {
          message.error('更新失败: ' + response.message);
        }
      } else {
        // 创建枚举值
        const response = await createCustomEnumValue(
          currentTypeId,
          values.name,
          values.description
        );
        
        if (response.success) {
          message.success('创建成功');
          setValueModalVisible(false);
          valueForm.resetFields();
          // 重新获取该类型的枚举值
          await fetchEnumValues(currentTypeId);
        } else {
          message.error('创建失败: ' + response.message);
        }
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 保存枚举类型
  const handleSaveType = async () => {
    try {
      const values = await typeForm.validateFields();
      
      if (editingType) {
        // 更新枚举类型
        const response = await updateCustomEnumType(
          editingType.id,
          values.name,
          values.scenarios,
        );
        
        if (response.success) {
          message.success('更新成功');
          setTypeModalVisible(false);
          typeForm.resetFields();
          // 重新获取枚举类型
          await fetchEnumTypes();
        } else {
          message.error('更新失败: ' + response.message);
        }
      } else {
        // 创建枚举类型
        const response = await createCustomEnumType(
          values.name,
          values.scenarios,
          values.mode,
          values.is_enabled
        );
        
        if (response.success) {
          message.success('创建成功');
          setTypeModalVisible(false);
          typeForm.resetFields();
          // 重新获取枚举类型
          await fetchEnumTypes();
        } else {
          message.error('创建失败: ' + response.message);
        }
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 保存自定义字段
  const handleSaveField = async () => {
    try {
      const values = await fieldForm.validateFields();
      
      if (editingField) {
        // 更新自定义字段
        const response = await updateCustomField(
          editingField.id,
          values.name,
          values.hint
        );
        
        if (response.success) {
          message.success('更新成功');
          setFieldModalVisible(false);
          fieldForm.resetFields();
          // 重新获取自定义字段
          await fetchCustomFields();
        } else {
          message.error('更新失败: ' + response.message);
        }
      } else {
        // 创建自定义字段
        const response = await createCustomField(
          values.name,
          values.hint,
          values.scenarios,
          values.is_enabled
        );
        
        if (response.success) {
          message.success('创建成功');
          setFieldModalVisible(false);
          fieldForm.resetFields();
          // 重新获取自定义字段
          await fetchCustomFields();
        } else {
          message.error('创建失败: ' + response.message);
        }
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };


  // 场景值与中文展示的映射关系
  const scenarioLabels: Record<CustomParamScenario, string> = {
    [CustomParamScenario.Appointment]: '预约场景',
    [CustomParamScenario.Treatment]: '治疗记录',
    [CustomParamScenario.PatientProfile]: '患者档案',
  };

  // 枚举值表格列定义
  const getValueColumns = (typeId: string) => [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (is_enabled: boolean, record: CustomEnumValue) => (
        <Switch 
          checked={is_enabled} 
          onChange={(checked) => handleValueEnabledChange(record, checked)}
          checkedChildren="启用" 
          unCheckedChildren="禁用" 
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CustomEnumValue) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEditValue(record)}
            size="small"
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  // 自定义字段表格列定义
  const getFieldColumns = () => [
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '提示信息',
      dataIndex: 'hint',
      key: 'hint',
    },
    {
      title: '适用场景',
      dataIndex: 'scenarios',
      key: 'scenarios',
      render: (scenarios: CustomParamScenario[]) => (
        <>
          {scenarios.map(s => <Tag color="blue">{scenarioLabels[s]}</Tag>)}
        </>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (is_enabled: boolean, record: CustomField) => (
        <Switch 
          checked={is_enabled} 
          onChange={(checked) => handleFieldEnabledChange(record, checked)}
          checkedChildren="启用" 
          unCheckedChildren="禁用" 
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CustomField) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEditField(record)}
            size="small"
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>参数管理</h1>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleCreateType}
        >
          添加枚举类型
        </Button>
      </div>
      {enumTypes.map(type => (
        <Card 
          key={type.id} 
          title={
          <>
            {type.name}&ensp;
            {type.scenarios.map(s => <Tag color="blue">{scenarioLabels[s]}</Tag>)}
            <Tag color={type.mode === CustomEnumMode.Single ? 'green' : 'orange'}>{type.mode === CustomEnumMode.Single ? '单选' : '多选'}</Tag>
            <Switch 
              checked={type.is_enabled} 
              onChange={(checked) => handleTypeEnabledChange(type, checked)}
              checkedChildren="启用" 
              unCheckedChildren="禁用" 
              style={{ marginLeft: 8 }}
            />
          </>
          }
          extra={
            <Space>
              <Button 
                icon={<EditOutlined />} 
                onClick={() => handleEditType(type)}
              >
                编辑类型
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => handleCreateValue(type.id)}
              >
                添加枚举值
              </Button>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Table 
            columns={getValueColumns(type.id)} 
            dataSource={enumValues[type.id] || []} 
            loading={loading}
            pagination={false}
            rowKey="id"
          />
        </Card>
      ))}

      {/* 自定义字段列表 */}
      <Card 
        title="自定义字段"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleCreateField}
          >
            添加自定义字段
          </Button>
        }
        style={{ marginTop: 24 }}
      >
        <Table 
          columns={getFieldColumns()} 
          dataSource={customFields} 
          loading={loading}
          pagination={false}
          rowKey="id"
        />
      </Card>

      <Modal
        title={editingValue ? "编辑枚举值" : "创建枚举值"}
        open={valueModalVisible}
        onOk={handleSaveValue}
        onCancel={() => {
          setValueModalVisible(false);
          valueForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={valueForm} initialValues={{is_enabled: true}} layout="vertical">
          <Form.Item 
            name="name" 
            label="名称" 
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea />
          </Form.Item>
          {editingValue === null && (
            <Form.Item 
              name="is_enabled" 
              label="是否启用" 
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={editingType ? "编辑枚举类型" : "创建枚举类型"}
        open={typeModalVisible}
        onOk={handleSaveType}
        onCancel={() => {
          setTypeModalVisible(false);
          typeForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={typeForm} initialValues={{is_enabled: true}} layout="vertical">
          <Form.Item 
            name="name" 
            label="名称" 
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="scenarios" label="场景">
            {editingType ? (
              // 编辑模式下，scenarios字段只读显示
              <div>
                {typeForm.getFieldValue('scenarios')?.map((scenario: CustomParamScenario) => (
                  <Tag key={scenario} color="blue">
                    {scenarioLabels[scenario]}
                  </Tag>
                ))}
                <Input type="hidden" />
              </div>
            ) : (
              // 创建模式下，可以编辑scenarios字段
              <Checkbox.Group>
                {Object.entries(scenarioLabels).map(([scenario, label]) => (
                  <Checkbox key={scenario} value={scenario}>
                    {label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            )}
          </Form.Item>
          <Form.Item name="mode" label="模式" rules={[{ required: true, message: '请选择模式' }]}>  
            {editingType ? (
              // 编辑模式下，mode字段只读显示
              <div>
                {typeForm.getFieldValue('mode') === 'single' ? '单选' : '多选'}
                <Input type="hidden" />
              </div>
            ) : (
              // 创建模式下，可以编辑mode字段
              <Radio.Group>
                <Radio value={CustomEnumMode.Single}>单选</Radio>
              <Radio value={CustomEnumMode.Multiple}>多选</Radio>
              </Radio.Group>
            )}
          </Form.Item>
          {editingType === null && (
            <Form.Item 
              name="is_enabled" 
              label="是否启用" 
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={editingField ? "编辑自定义字段" : "创建自定义字段"}
        open={fieldModalVisible}
        onOk={handleSaveField}
        onCancel={() => {
          setFieldModalVisible(false);
          fieldForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={fieldForm} initialValues={{is_enabled: true}} layout="vertical">
          <Form.Item 
            name="name" 
            label="名称" 
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="hint" label="提示信息">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="scenarios" label="适用场景" rules={[{ required: true, message: '请选择适用场景' }]}>  
            <Checkbox.Group>
              {Object.entries(scenarioLabels).map(([scenario, label]) => (
                <Checkbox key={scenario} value={scenario as CustomParamScenario}>
                  {label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          {editingField === null && (
            <Form.Item 
              name="is_enabled" 
              label="是否启用" 
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CustomEnumManagement;